"use client";

import { formatCurrency, formatPercent, cn } from "@/lib/utils";
import Link from "next/link";
import { useEffect, useState, useCallback } from "react";
import { dashboardApi } from "@/lib/api";

// Types for API responses
interface AllocationItem {
  name: string;
  value: number;
  pct: number;
  color: string;
}

interface Engine {
  name: string;
  status: string;
  pnl: number;
  pnlPct: number;
  positions: number;
  trades: number;
  winRate: number;
  color: string;
  chartData: number[];
}

interface Position {
  id: string;
  engine: string;
  symbol: string;
  side: string;
  entry_price: number;
  current_price: number;
  pnl: number;
  pnl_pct: number;
  duration: string;
}

interface Signal {
  id: string;
  icon: string;
  title: string;
  description: string;
  time: string;
  strength: number;
  type: string;
}

interface MarketItem {
  symbol: string;
  name: string;
  price: number;
  change_pct: number;
}

interface TodayStats {
  win_rate: number;
  profit_factor: number;
  sharpe_ratio: number;
  total_trades: number;
  avg_hold_time: string;
  max_drawdown: number;
}

interface DashboardData {
  portfolio: {
    total_value: number;
    pnl_today: number;
    pnl_today_pct: number;
    allocation: {
      trencher: number;
      trencher_value: number;
      equities: number;
      equities_value: number;
      options: number;
      options_value: number;
      cash_reserve: number;
      cash_reserve_value: number;
    };
  };
  engines: any[];
  positions: Position[];
  signals: any[];
  market_overview: MarketItem[];
  today_stats: TodayStats;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 18) return "Good Afternoon";
  return "Good Evening";
}

function formatDateTime() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) + 
    ' • ' + new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' EST';
}

function getSignalIcon(type: string): string {
  switch(type) {
    case 'whale_buy': return '🐋';
    case 'whale_confluence': return '🐋';
    case 'breakout': return '📈';
    case 'risk_alert': return '⚠️';
    case 'sentiment': return '📊';
    default: return '📡';
  }
}

function getEngineColor(name: string): string {
  switch(name.toLowerCase()) {
    case 'trencher': return '#10b981';
    case 'equities': return '#3b82f6';
    case 'options': return '#f59e0b';
    default: return '#6b7280';
  }
}

function timeAgo(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Dashboard() {
  const [dateTime, setDateTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await dashboardApi.getDashboard();
      setData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err: any) {
      console.error('Error fetching dashboard:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    setDateTime(formatDateTime());
    const timeInterval = setInterval(() => setDateTime(formatDateTime()), 60000);
    
    // Initial fetch
    fetchDashboard();
    
    // Refresh every 30 seconds
    const refreshInterval = setInterval(fetchDashboard, 30000);
    
    return () => {
      clearInterval(timeInterval);
      clearInterval(refreshInterval);
    };
  }, [fetchDashboard]);

  // Format allocation data
  const allocation: AllocationItem[] = data ? [
    { name: "Trencher", value: data.portfolio.allocation.trencher_value, pct: Math.round(data.portfolio.allocation.trencher * 100), color: "#10b981" },
    { name: "Equities", value: data.portfolio.allocation.equities_value, pct: Math.round(data.portfolio.allocation.equities * 100), color: "#3b82f6" },
    { name: "Options", value: data.portfolio.allocation.options_value, pct: Math.round(data.portfolio.allocation.options * 100), color: "#14b8a6" },
    { name: "Cash", value: data.portfolio.allocation.cash_reserve_value, pct: Math.round(data.portfolio.allocation.cash_reserve * 100), color: "#6b7280" },
  ] : [];

  // Format engines data
  const engines: Engine[] = data?.engines?.map(e => ({
    name: e.name,
    status: e.status,
    pnl: e.pnl_today,
    pnlPct: e.pnl_today_pct,
    positions: e.open_positions,
    trades: e.trades_today,
    winRate: e.win_rate,
    color: getEngineColor(e.name),
    chartData: e.chart_data || [0, 0, 0, 0, 0, 0, 0],
  })) || [];

  // Format positions
  const positions = data?.positions || [];

  // Format signals  
  const signals = data?.signals?.map(s => ({
    id: s.id,
    icon: getSignalIcon(s.type),
    title: s.title || `${s.type}: ${s.symbol}`,
    desc: s.description,
    time: timeAgo(s.timestamp),
    strength: s.strength,
    type: s.type,
  })) || [];

  // Format markets
  const markets = data?.market_overview || [];

  // Today's stats
  const todayStats = data?.today_stats || {
    win_rate: 0,
    profit_factor: 0,
    sharpe_ratio: 0,
    total_trades: 0,
    avg_hold_time: "0m",
    max_drawdown: 0,
  };

  if (loading) {
    return (
      <div className="p-6 pb-24 flex items-center justify-center min-h-screen">
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid #1e1e24',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#71717a' }}>Loading dashboard...</p>
          <style jsx>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6 pb-24 flex items-center justify-center min-h-screen">
        <div style={{ textAlign: 'center', background: '#111114', padding: '32px', borderRadius: '12px', border: '1px solid #ef4444' }}>
          <p style={{ fontSize: '20px', color: '#ef4444', marginBottom: '16px' }}>⚠️ Error Loading Dashboard</p>
          <p style={{ color: '#71717a', marginBottom: '16px' }}>{error}</p>
          <button 
            onClick={fetchDashboard}
            style={{ padding: '8px 16px', background: '#6366f1', color: 'white', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const portfolioValue = data?.portfolio.total_value || 0;
  const pnlToday = data?.portfolio.pnl_today || 0;
  const pnlTodayPct = data?.portfolio.pnl_today_pct || 0;

  return (
    <div className="p-6 pb-24">
      {/* Connection Status */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,0.2)', border: '1px solid #ef4444', borderRadius: '8px', padding: '8px 16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#ef4444', fontSize: '14px' }}>⚠️ Connection issue - showing cached data</span>
          <button onClick={fetchDashboard} style={{ color: '#ef4444', fontSize: '12px', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
        </div>
      )}
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', marginBottom: '4px' }}>{getGreeting()}, Trader</h1>
          <p style={{ fontSize: '14px', color: '#71717a' }}>{dateTime}</p>
          {lastUpdate && (
            <p style={{ fontSize: '12px', color: '#52525b', marginTop: '4px' }}>
              Last updated: {lastUpdate.toLocaleTimeString()} • 
              <span style={{ color: '#10b981' }}> Live</span>
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }}>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '12px', color: '#71717a', marginBottom: '4px' }}>Total Portfolio Value</p>
            <p style={{ fontSize: '28px', fontWeight: 'bold', color: 'white' }}>{formatCurrency(portfolioValue)}</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '12px', color: '#71717a', marginBottom: '4px' }}>Today&apos;s P&L</p>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: pnlToday >= 0 ? '#10b981' : '#ef4444' }}>
              {pnlToday >= 0 ? '+' : ''}{formatCurrency(pnlToday)} ({formatPercent(pnlTodayPct)})
            </p>
          </div>
        </div>
      </div>

      {/* Allocation */}
      <div style={{ background: '#111114', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #1e1e24' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Portfolio Allocation</h2>
          <button style={{ fontSize: '12px', color: '#71717a', background: '#1a1a1f', padding: '6px 12px', borderRadius: '8px', border: '1px solid #27272a' }}>↻ Rebalance</button>
        </div>
        <div style={{ display: 'flex', height: '10px', borderRadius: '9999px', overflow: 'hidden', marginBottom: '16px' }}>
          {allocation.map((a, i) => (
            <div key={i} style={{ width: `${a.pct}%`, backgroundColor: a.color }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {allocation.map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '4px', backgroundColor: a.color }} />
              <div>
                <p style={{ fontSize: '12px', color: '#71717a' }}>{a.name} ({a.pct}%)</p>
                <p style={{ fontSize: '14px', fontWeight: '600', color: 'white' }}>{formatCurrency(a.value)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Engine Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
        {engines.map((e, i) => (
          <div key={i} style={{ background: '#111114', borderRadius: '12px', padding: '16px', border: '1px solid #1e1e24' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '4px', height: '20px', borderRadius: '2px', backgroundColor: e.color }} />
                <span style={{ fontWeight: '600', color: 'white' }}>{e.name}</span>
              </div>
              <span style={{ fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', backgroundColor: e.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)', color: e.status === 'active' ? '#10b981' : '#f59e0b' }}>{e.status}</span>
            </div>
            <p style={{ fontSize: '24px', fontWeight: 'bold', color: e.pnl >= 0 ? '#10b981' : '#ef4444', marginBottom: '4px' }}>{e.pnl >= 0 ? '+' : ''}{formatCurrency(e.pnl)}</p>
            <p style={{ fontSize: '14px', color: e.pnl >= 0 ? 'rgba(16,185,129,0.7)' : 'rgba(239,68,68,0.7)', marginBottom: '12px' }}>({formatPercent(e.pnlPct)})</p>
            <p style={{ fontSize: '12px', color: '#71717a', marginBottom: '12px' }}>{e.positions} Open | {e.trades} Today | <span style={{ color: '#10b981' }}>{e.winRate}% Win</span></p>
            <div style={{ display: 'flex', height: '40px', alignItems: 'flex-end', gap: '2px', marginBottom: '12px' }}>
              {e.chartData.map((h, j) => {
                const maxVal = Math.max(...e.chartData.map(Math.abs), 1);
                const normalizedHeight = Math.abs(h) / maxVal * 100;
                return (
                  <div key={j} style={{ flex: 1, height: `${Math.max(normalizedHeight, 10)}%`, backgroundColor: h >= 0 ? e.color + '60' : '#ef444460', borderRadius: '2px 2px 0 0' }} />
                );
              })}
            </div>
            <Link href={`/${e.name.toLowerCase()}`} style={{ display: 'block', textAlign: 'center', padding: '8px', fontSize: '14px', borderRadius: '8px', backgroundColor: e.color + '20', color: e.color, textDecoration: 'none' }}>View Engine</Link>
          </div>
        ))}
      </div>

      {/* Open Positions */}
      <div style={{ background: '#111114', borderRadius: '12px', marginBottom: '16px', border: '1px solid #1e1e24', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e24', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontWeight: '600', color: 'white' }}>Open Positions ({positions.length})</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <select style={{ fontSize: '12px', background: '#1a1a1f', border: '1px solid #27272a', borderRadius: '6px', padding: '4px 8px', color: '#71717a' }}><option>All Engines</option></select>
            {positions.length > 0 && (
              <button style={{ fontSize: '12px', background: 'rgba(239,68,68,0.2)', color: '#ef4444', padding: '4px 12px', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.3)' }}>✕ Close All</button>
            )}
          </div>
        </div>
        {positions.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#71717a' }}>
            <p style={{ fontSize: '14px' }}>No open positions</p>
            <p style={{ fontSize: '12px', marginTop: '4px' }}>Go to an engine to place trades</p>
          </div>
        ) : (
          <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#0d0d10' }}>
              <tr style={{ fontSize: '12px', color: '#71717a' }}>
                <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: '500' }}>ENGINE</th>
                <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: '500' }}>SYMBOL</th>
                <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: '500' }}>SIDE</th>
                <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: '500' }}>ENTRY</th>
                <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: '500' }}>CURRENT</th>
                <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: '500' }}>P&L</th>
                <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: '500' }}>P&L %</th>
                <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: '500' }}>TIME</th>
                <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: '500' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p, i) => (
                <tr key={p.id || i} style={{ borderTop: '1px solid #1e1e24' }}>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '500', padding: '2px 8px', borderRadius: '4px', backgroundColor: p.engine === 'trencher' ? 'rgba(16,185,129,0.2)' : p.engine === 'equities' ? 'rgba(59,130,246,0.2)' : 'rgba(245,158,11,0.2)', color: p.engine === 'trencher' ? '#10b981' : p.engine === 'equities' ? '#3b82f6' : '#f59e0b' }}>{p.engine}</span>
                  </td>
                  <td style={{ padding: '10px 16px', fontWeight: '500', color: 'white' }}>{p.symbol}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', backgroundColor: p.side === 'long' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)', color: p.side === 'long' ? '#10b981' : '#ef4444' }}>{p.side}</span>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#71717a' }}>${p.entry_price < 1 ? p.entry_price?.toFixed(6) : p.entry_price?.toFixed(2)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: 'white' }}>${p.current_price < 1 ? p.current_price?.toFixed(6) : p.current_price?.toFixed(2)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: p.pnl >= 0 ? '#10b981' : '#ef4444' }}>{p.pnl >= 0 ? '+' : ''}{formatCurrency(p.pnl)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: p.pnl_pct >= 0 ? '#10b981' : '#ef4444' }}>{formatPercent(p.pnl_pct)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#71717a' }}>{p.duration}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <button style={{ fontSize: '12px', padding: '4px 8px', background: '#1a1a1f', borderRadius: '4px', color: '#71717a', border: 'none', cursor: 'pointer' }}>Close</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Three Columns: Signals, Market, Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '5fr 4fr 3fr', gap: '16px', marginBottom: '16px' }}>
        {/* Live Signals */}
        <div style={{ background: '#111114', borderRadius: '12px', border: '1px solid #1e1e24' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #1e1e24', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: '600', color: 'white' }}>Live Signals</span>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', animation: 'pulse 2s infinite' }} />
            </div>
            <Link href="/signals" style={{ fontSize: '12px', color: '#71717a', textDecoration: 'none' }}>View All →</Link>
          </div>
          <div style={{ padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
            {signals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#71717a' }}>
                <p>No active signals</p>
                <p style={{ fontSize: '12px', marginTop: '4px' }}>Signals will appear here when detected</p>
              </div>
            ) : (
              signals.map((s) => (
                <div key={s.id} style={{ padding: '12px', background: '#0d0d10', borderRadius: '8px', marginBottom: '8px', borderLeft: `4px solid ${s.type === 'risk_alert' ? '#ef4444' : s.strength >= 80 ? '#10b981' : '#3b82f6'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '16px' }}>{s.icon}</span>
                      <div>
                        <p style={{ fontSize: '13px', fontWeight: '500', color: 'white' }}>{s.title}</p>
                        <p style={{ fontSize: '11px', color: '#71717a' }}>{s.desc}</p>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: '11px' }}>
                      <p style={{ color: '#71717a' }}>{s.time}</p>
                      {s.strength > 0 && <p style={{ color: s.strength >= 70 ? '#10b981' : '#71717a' }}>Str: {s.strength}</p>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '4px', background: s.type === 'risk_alert' ? 'rgba(239,68,68,0.2)' : 'rgba(99,102,241,0.2)', color: s.type === 'risk_alert' ? '#ef4444' : '#6366f1', border: 'none' }}>{s.type === 'risk_alert' ? 'Review' : 'Execute'}</button>
                    <button style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '4px', background: '#1a1a1f', color: '#71717a', border: 'none' }}>Details</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Market Overview */}
        <div style={{ background: '#111114', borderRadius: '12px', border: '1px solid #1e1e24', padding: '16px' }}>
          <h3 style={{ fontWeight: '600', color: 'white', marginBottom: '12px' }}>Market Overview</h3>
          {markets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: '#71717a' }}>
              <p>Loading market data...</p>
            </div>
          ) : (
            markets.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < markets.length - 1 ? '1px solid #1e1e24' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: m.change_pct >= 0 ? '#10b981' : '#ef4444' }} />
                  <span style={{ fontSize: '14px', color: 'white' }}>{m.symbol}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '14px', color: 'white' }}>${m.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                  <p style={{ fontSize: '12px', color: m.change_pct >= 0 ? '#10b981' : '#ef4444' }}>{m.change_pct >= 0 ? '↗' : '↘'} {formatPercent(m.change_pct)}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Today's Stats */}
        <div style={{ background: '#111114', borderRadius: '12px', border: '1px solid #1e1e24', padding: '16px' }}>
          <h3 style={{ fontWeight: '600', color: 'white', marginBottom: '12px' }}>Today&apos;s Stats</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span style={{ fontSize: '13px', color: '#71717a' }}>Win Rate</span><span style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>{todayStats.win_rate}%</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span style={{ fontSize: '13px', color: '#71717a' }}>Profit Factor</span><span style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>{todayStats.profit_factor}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}><span style={{ fontSize: '13px', color: '#71717a' }}>Sharpe Ratio</span><span style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>{todayStats.sharpe_ratio}</span></div>
          <div style={{ borderTop: '1px solid #1e1e24', paddingTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', textAlign: 'center' }}>
            <div><p style={{ fontSize: '11px', color: '#71717a' }}>Total Trades</p><p style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>{todayStats.total_trades}</p></div>
            <div><p style={{ fontSize: '11px', color: '#71717a' }}>Avg Hold</p><p style={{ fontSize: '18px', fontWeight: 'bold', color: 'white' }}>{todayStats.avg_hold_time}</p></div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '8px' }}><p style={{ fontSize: '11px', color: '#71717a' }}>Max Drawdown</p><p style={{ fontSize: '16px', fontWeight: 'bold', color: todayStats.max_drawdown < 0 ? '#ef4444' : 'white' }}>{todayStats.max_drawdown}%</p></div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ position: 'fixed', bottom: 0, left: '64px', right: 0, background: 'rgba(13,13,16,0.95)', backdropFilter: 'blur(8px)', borderTop: '1px solid #1e1e24', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 40 }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: 'white' }}>Quick Actions</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={{ padding: '8px 16px', background: '#6366f1', color: 'white', fontSize: '14px', fontWeight: '500', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>+ New Trade</button>
          <Link href="/scanner" style={{ padding: '8px 16px', background: '#1a1a1f', color: '#71717a', fontSize: '14px', borderRadius: '8px', border: '1px solid #27272a', textDecoration: 'none' }}>🔍 Scanner</Link>
          <Link href="/alerts" style={{ padding: '8px 16px', background: '#1a1a1f', color: '#71717a', fontSize: '14px', borderRadius: '8px', border: '1px solid #27272a', textDecoration: 'none' }}>🔔 Alerts</Link>
          <Link href="/journal" style={{ padding: '8px 16px', background: '#1a1a1f', color: '#71717a', fontSize: '14px', borderRadius: '8px', border: '1px solid #27272a', textDecoration: 'none' }}>📓 Journal</Link>
          <Link href="/risk" style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.2)', color: '#ef4444', fontSize: '14px', fontWeight: '500', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.3)', textDecoration: 'none' }}>Kill Switch</Link>
        </div>
      </div>

      <style jsx global>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
